import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/styles';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { withRouter } from 'react-router-dom';
import {
  Grid,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  ExpansionPanel,
  ExpansionPanelSummary,
  ExpansionPanelDetails,
  IconButton,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from '@material-ui/core';
import {
  BlockOutlined,
  CheckCircleOutlineOutlined,
  ExpandMore,
  AddCircleOutlineOutlined,
  EditOutlined,
  DeleteOutlined,
  ClearOutlined,
  CheckOutlined,
  CalendarToday,
} from '@material-ui/icons/';
import clsx from 'clsx';
import DatePicker from 'react-datepicker';
import styles from '../../../style';
import moment from 'moment';
import { connect } from 'react-redux';
import {
  saveAllergy,
  updateAllergy,
  deleteAllergy,
  saveSocialHistory,
  updateSocialHistory,
  deleteSocialHistory,
  saveFamilyHistory,
  updateFamilyHistory,
  deleteFamilyHistory,
  saveAdvanceDirective,
  updateAdvanceDirective,
  deleteAdvanceDirective,
  saveAdditionalItems,
  updateAdditionalItems,
  deleteAdditionalItems,
  saveHealthMaintenance,
  updateHealthMaintenance,
  deleteHealthMaintenance,
  saveVaccinations,
  updateVaccinations,
  deleteVaccinations,
  saveMedication,
  updateMedication,
  inactivateMedication,
  activateMedication,
  deleteMedication,
  getPatientInactiveDiagnosis,
  getPatientActiveMedications,
  getPatientMedicalHistory,
  getPatientInactiveMedication,
} from 'services/soap/action';

// ---------------------------------------------------------------------------
// Tiny helper: wraps an IconButton with a MUI Tooltip.
// ---------------------------------------------------------------------------
const TipButton = ({ title, children, ...rest }) => (
  <Tooltip title={title} arrow>
    <span style={{ display: 'inline-flex' }}>
      <IconButton {...rest}>{children}</IconButton>
    </span>
  </Tooltip>
);

// ---------------------------------------------------------------------------
// Shared inline spinner for save/confirm and update/confirm icon buttons
// ---------------------------------------------------------------------------
const SaveIcon = ({ loading }) =>
  loading ? <CircularProgress size={16} color="inherit" /> : <CheckOutlined />;

const MHxSoap = props => {
  const {
    classes,
    getPatientActiveMedications,
    getPatientMedicalHistory,
    getPatientInactiveMedication,
    patientId,
    ptMedicalHistory,
    ptInactiveMedication,
    ptActiveMedication,
  } = props;

  // Shared delete-dialog loading state (all sections except medications)
  const [isDeletingItem, setIsDeletingItem] = useState(false);

  // ---------------------------------------------------------------------------
  // #region Allergy  (unchanged — kept as-is per requirement)
  // ---------------------------------------------------------------------------
  const initialUserStateAllergies = { id: 0, name: '', reaction: '' };
  const [valuesAllergy, setValuesAllergy] = useState(initialUserStateAllergies);
  const [editvaluesAllergy, setEditValuesAllergy] = useState(initialUserStateAllergies);
  const [allergy, setAllergy] = useState(null);
  const [isaddallergy, setAddAllergy] = useState(false);
  const [isEditAllergy, setIsEditAllergy] = useState(false);
  const [loadingAllergy, setLoadingAllergy] = useState(null);

  const handleFieldChange = (field, value) =>
    setValuesAllergy({ ...valuesAllergy, [field]: value });

  const handleEditFieldChange = (field, value) =>
    setEditValuesAllergy({ ...editvaluesAllergy, [field]: value });

  const handleAddAllergy = data => {
    setAddAllergy(true);
    if (data) setValuesAllergy({ id: data.id, name: data.name, reaction: data.reaction });
  };

  const handleAddAllergyCancel = () => {
    setAddAllergy(false);
    setValuesAllergy(initialUserStateAllergies);
  };

  const handleSaveAllergy = async () => {
    if (valuesAllergy.name || valuesAllergy.reaction) {
      setLoadingAllergy('save');
      try {
        const res = await props.saveAllergy(patientId, valuesAllergy);
        if (res.status) {
          setAllergy(res.allergies);
          setValuesAllergy(initialUserStateAllergies);
        }
      } finally {
        setLoadingAllergy(null);
        setAddAllergy(false);
        getPatientMedicalHistory(patientId);
      }
    } else {
      setAddAllergy(false);
    }
  };

  const handleUpdateAllergy = async idx => {
    if (editvaluesAllergy.name || editvaluesAllergy.reaction) {
      setLoadingAllergy('update');
      try {
        const res = await props.updateAllergy(patientId, editvaluesAllergy);
        if (res.status) {
          const newState = [...allergy];
          newState[idx] = res.allergy;
          setAllergy(newState);
          setIsEditAllergy(false);
          getPatientMedicalHistory(patientId);
        }
      } finally {
        setLoadingAllergy(null);
      }
    }
  };

  const handleEditAllergy = (data, index) => {
    setIsEditAllergy(true);
    if (data)
      setEditValuesAllergy({ id: data.id, name: data.name, reaction: data.reaction, editIndex: index });
  };

  const clearAllergy = () => {
    setIsEditAllergy(false);
    setValuesAllergy(initialUserStateAllergies);
  };

  const _handleAddAllergyClick = event => {
    event.stopPropagation();
    handleAddAllergy();
  };

  const [handledeleteAllergy, setHandleDeleteAllergy] = useState({ showDeletePopup: false, itemId: '' });
  const handleOpenDeleteAllergy = id => setHandleDeleteAllergy({ showDeletePopup: true, itemId: id });
  const handleCancelDeleteAllergy = () => setHandleDeleteAllergy({ showDeletePopup: false });

  const handleDeleteAllergyOk = async itemId => {
    setIsDeletingItem(true);
    try {
      const res = await props.deleteAllergy(patientId, itemId);
      if (res.status) {
        setAllergy(res.allergies);
        setValuesAllergy(initialUserStateAllergies);
      }
    } finally {
      setIsDeletingItem(false);
      setHandleDeleteAllergy({ showDeletePopup: false });
    }
  };

  const renderDeleteAllergyDialogue = () => (
    <Dialog aria-labelledby="confirmation-dialog-title" disableBackdropClick disableEscapeKeyDown maxWidth="xs" open={handledeleteAllergy.showDeletePopup}>
      <DialogTitle id="confirmation-dialog-title">Patient MedicalHx Delete Confirmation</DialogTitle>
      <DialogContent><DialogContentText>Are you sure you want to delete allergy?</DialogContentText></DialogContent>
      <DialogActions>
        <Button color="primary" disabled={isDeletingItem} onClick={() => handleDeleteAllergyOk(handledeleteAllergy.itemId)} style={{ minWidth: 64 }}>
          {isDeletingItem ? <CircularProgress size={18} color="inherit" /> : 'Yes'}
        </Button>
        <Button color="primary" disabled={isDeletingItem} onClick={handleCancelDeleteAllergy}>No</Button>
      </DialogActions>
    </Dialog>
  );
  // #endregion

  // ---------------------------------------------------------------------------
  // #region Social History
  // ---------------------------------------------------------------------------
  const initialUserStateSH = { id: 0, description: '' };
  const [valuesSH, setValuesSH] = useState(initialUserStateSH);
  const [editvaluesSH, setEditValuesSH] = useState(initialUserStateSH);
  const [SH, setSH] = useState(props.socialHistory || []);
  const [isaddSH, setIsAddSH] = useState(false);
  const [isEditSH, setIsEditSH] = useState(false);
  const [loadingSH, setLoadingSH] = useState(null);

  const handleFieldChangeSH = (field, value) => setValuesSH({ ...valuesSH, [field]: value });
  const handleEditFieldChangeSH = (field, value) => setEditValuesSH({ ...editvaluesSH, [field]: value });

  const handleAddSH = data => {
    setIsAddSH(true);
    if (data) setValuesSH({ id: data.id, description: data.description });
  };
  const handleAddSHCancel = () => { setIsAddSH(false); setValuesSH(initialUserStateSH); };

  const handleSaveSocialHistory = async () => {
    if (valuesSH.description) {
      setLoadingSH('save');
      try {
        const res = await props.saveSocialHistory(patientId, valuesSH);
        if (res.status) {
          if (res.socialHistory) {
            // API returned full list — use it
            setSH(res.socialHistory);
            getPatientMedicalHistory(patientId);
          } else {
            // API did NOT return the list — append optimistically and refresh
            setSH(prev => [...(prev || []), { ...valuesSH, id: res.id || Date.now() }]);
            getPatientMedicalHistory(patientId);
          }
          setValuesSH(initialUserStateSH);
        }
      } finally {
        setLoadingSH(null);
        setIsAddSH(false);
      }
    } else {
      setIsAddSH(false);
    }
  };

  const handleUpdateSocialHistory = async idx => {
    if (editvaluesSH.description) {
      setLoadingSH('update');
      try {
        const res = await props.updateSocialHistory(patientId, editvaluesSH);
        if (res.status) {
          // Optimistically update the row in place
          setSH(prev => prev.map(item =>
            item.id === editvaluesSH.id ? { ...item, description: editvaluesSH.description } : item
          ));
          getPatientMedicalHistory(patientId);
          setIsEditSH(false);
        }
      } finally {
        setLoadingSH(null);
      }
    }
  };

  const handleEditSocialHistory = (data, index) => {
    setIsEditSH(true);
    if (data) setEditValuesSH({ id: data.id, description: data.description, editIndex: index });
  };
  const clearSocialHistory = () => { setIsEditSH(false); setValuesSH(initialUserStateSH); };
  const _handleAddSHClick = event => { event.stopPropagation(); handleAddSH(); };

  const [handledeleteSH, setHandleDeleteSH] = useState({ showDeletePopupSH: false, itemId: '' });
  const handleOpenDeleteSH = id => setHandleDeleteSH({ showDeletePopupSH: true, itemId: id });
  const handleCancelDeleteSH = () => setHandleDeleteSH({ showDeletePopupSH: false });

  const handleDeleteSHOk = async itemId => {
    setIsDeletingItem(true);
    try {
      const res = await props.deleteSocialHistory(patientId, itemId);
      if (res.status) {
        if (res.socialHistory) {
          setSH(res.socialHistory);
        } else {
          setSH(prev => prev.filter(item => item.id !== itemId));
          getPatientMedicalHistory(patientId);
        }
        setValuesSH(initialUserStateSH);
      }
    } finally {
      setIsDeletingItem(false);
      setHandleDeleteSH({ showDeletePopupSH: false });
    }
  };

  const renderDeleteSHDialogue = () => (
    <Dialog aria-labelledby="confirmation-dialog-title" disableBackdropClick disableEscapeKeyDown maxWidth="xs" open={handledeleteSH.showDeletePopupSH}>
      <DialogTitle id="confirmation-dialog-title">Patient MedicalHx Delete Confirmation</DialogTitle>
      <DialogContent><DialogContentText>Are you sure you want to delete social history?</DialogContentText></DialogContent>
      <DialogActions>
        <Button color="primary" disabled={isDeletingItem} onClick={() => handleDeleteSHOk(handledeleteSH.itemId)} style={{ minWidth: 64 }}>
          {isDeletingItem ? <CircularProgress size={18} color="inherit" /> : 'Yes'}
        </Button>
        <Button color="primary" disabled={isDeletingItem} onClick={handleCancelDeleteSH}>No</Button>
      </DialogActions>
    </Dialog>
  );
  // #endregion

  // ---------------------------------------------------------------------------
  // #region Family History
  // ---------------------------------------------------------------------------
  const initialUserStateFH = { id: 0, description: '' };
  const [valuesFH, setValuesFH] = useState(initialUserStateFH);
  const [editvaluesFH, setEditValuesFH] = useState(initialUserStateFH);
  const [FH, setFH] = useState(null);
  const [isaddFH, setIsAddFH] = useState(false);
  const [isEditFH, setIsEditFH] = useState(false);
  const [loadingFH, setLoadingFH] = useState(null);

  const handleFieldChangeFH = (field, value) => setValuesFH({ ...valuesFH, [field]: value });
  const handleEditFieldChangeFH = (field, value) => setEditValuesFH({ ...editvaluesFH, [field]: value });

  const handleAddFH = data => {
    setIsAddFH(true);
    if (data) setValuesFH({ id: data.id, description: data.description });
  };
  const handleAddFHCancel = () => { setIsAddFH(false); setValuesFH(initialUserStateFH); };

  const handleSaveFamilyHistory = async () => {
    if (valuesFH.description) {
      setLoadingFH('save');
      try {
        const res = await props.saveFamilyHistory(patientId, valuesFH);
        if (res.status) {
          if (res.familyHistory) {
            setFH(res.familyHistory);
            getPatientMedicalHistory(patientId);
          } else {
            setFH(prev => [...(prev || []), { ...valuesFH, id: res.id || Date.now() }]);
            getPatientMedicalHistory(patientId);
          }
          setValuesFH(initialUserStateFH);
        }
      } finally {
        setLoadingFH(null);
        setIsAddFH(false);
      }
    } else {
      setIsAddFH(false);
    }
  };

  const handleUpdateFamilyHistory = async idx => {
    if (editvaluesFH.description) {
      setLoadingFH('update');
      try {
        const res = await props.updateFamilyHistory(patientId, editvaluesFH);
        if (res.status) {
          setFH(prev => prev.map(item =>
            item.id === editvaluesFH.id ? { ...item, description: editvaluesFH.description } : item
          ));
          getPatientMedicalHistory(patientId);
          setIsEditFH(false);
        }
      } finally {
        setLoadingFH(null);
      }
    }
  };

  const handleEditFamilyHistory = (data, index) => {
    setIsEditFH(true);
    if (data) setEditValuesFH({ id: data.id, description: data.description, editIndex: index });
  };
  const clearFamilyHistory = () => { setIsEditFH(false); setValuesFH(initialUserStateFH); };
  const _handleAddFHClick = event => { event.stopPropagation(); handleAddFH(); };

  const [handledeleteFH, setHandleDeleteFH] = useState({ showDeletePopupFH: false, itemId: '' });
  const handleOpenDeleteFH = id => setHandleDeleteFH({ showDeletePopupFH: true, itemId: id });
  const handleCancelDeleteFH = () => setHandleDeleteFH({ showDeletePopupFH: false });

  const handleDeleteFHOk = async itemId => {
    setIsDeletingItem(true);
    try {
      const res = await props.deleteFamilyHistory(patientId, itemId);
      if (res.status) {
        if (res.familyHistory) {
          setFH(res.familyHistory);
        } else {
          setFH(prev => prev.filter(item => item.id !== itemId));
          getPatientMedicalHistory(patientId);
        }
        setValuesFH(initialUserStateFH);
      }
    } finally {
      setIsDeletingItem(false);
      setHandleDeleteFH({ showDeletePopupFH: false });
    }
  };

  const renderDeleteFHDialogue = () => (
    <Dialog aria-labelledby="confirmation-dialog-title" disableBackdropClick disableEscapeKeyDown maxWidth="xs" open={handledeleteFH.showDeletePopupFH}>
      <DialogTitle id="confirmation-dialog-title">Patient MedicalHx Delete Confirmation</DialogTitle>
      <DialogContent><DialogContentText>Are you sure you want to delete family history?</DialogContentText></DialogContent>
      <DialogActions>
        <Button color="primary" disabled={isDeletingItem} onClick={() => handleDeleteFHOk(handledeleteFH.itemId)} style={{ minWidth: 64 }}>
          {isDeletingItem ? <CircularProgress size={18} color="inherit" /> : 'Yes'}
        </Button>
        <Button color="primary" disabled={isDeletingItem} onClick={handleCancelDeleteFH}>No</Button>
      </DialogActions>
    </Dialog>
  );
  // #endregion

  // ---------------------------------------------------------------------------
  // #region Advance Directives
  // ---------------------------------------------------------------------------
  const initialUserStateAD = { id: 0, description: '' };
  const [valuesAD, setValuesAD] = useState(initialUserStateAD);
  const [editvaluesAD, setEditValuesAD] = useState(initialUserStateAD);
  const [AD, setAD] = useState(null);
  const [isaddAD, setIsAddAD] = useState(false);
  const [isEditAD, setIsEditAD] = useState(false);
  const [loadingAD, setLoadingAD] = useState(null);

  const handleFieldChangeAD = (field, value) => setValuesAD({ ...valuesAD, [field]: value });
  const handleEditFieldChangeAD = (field, value) => setEditValuesAD({ ...editvaluesAD, [field]: value });

  const handleAddAD = data => {
    setIsAddAD(true);
    if (data) setValuesAD({ id: data.id, description: data.description });
  };
  const handleAddADCancel = () => { setIsAddAD(false); setValuesAD(initialUserStateAD); };

  const handleSaveAdvanceDirective = async () => {
    if (valuesAD.description) {
      setLoadingAD('save');
      try {
        const res = await props.saveAdvanceDirective(patientId, valuesAD);
        if (res.status) {
          if (res.advanceDirective) {
            setAD(res.advanceDirective);
            getPatientMedicalHistory(patientId);
          } else {
            setAD(prev => [...(prev || []), { ...valuesAD, id: res.id || Date.now() }]);
            getPatientMedicalHistory(patientId);
          }
          setValuesAD(initialUserStateAD);
        }
      } finally {
        setLoadingAD(null);
        setIsAddAD(false);
      }
    } else {
      setIsAddAD(false);
    }
  };

  const handleUpdateAdvanceDirective = async idx => {
    if (editvaluesAD.description) {
      setLoadingAD('update');
      try {
        const res = await props.updateAdvanceDirective(patientId, editvaluesAD);
        if (res.status) {
          setAD(prev => prev.map(item =>
            item.id === editvaluesAD.id ? { ...item, description: editvaluesAD.description } : item
          ));
          getPatientMedicalHistory(patientId);
          setIsEditAD(false);
        }
      } finally {
        setLoadingAD(null);
      }
    }
  };

  const handleEditAdvanceDirective = (data, index) => {
    setIsEditAD(true);
    if (data) setEditValuesAD({ id: data.id, description: data.description, editIndex: index });
  };
  const clearAdvanceDirective = () => { setIsEditAD(false); setValuesAD(initialUserStateAD); };
  const _handleAddADClick = event => { event.stopPropagation(); handleAddAD(); };

  const [handledeleteAD, setHandleDeleteAD] = useState({ showDeletePopupAD: false, itemId: '' });
  const handleOpenDeleteAD = id => setHandleDeleteAD({ showDeletePopupAD: true, itemId: id });
  const handleCancelDeleteAD = () => setHandleDeleteAD({ showDeletePopupAD: false });

  const handleDeleteADOk = async itemId => {
    setIsDeletingItem(true);
    try {
      const res = await props.deleteAdvanceDirective(patientId, itemId);
      if (res.status) {
        if (res.advanceDirective) {
          setAD(res.advanceDirective);
        } else {
          setAD(prev => prev.filter(item => item.id !== itemId));
          getPatientMedicalHistory(patientId);
        }
        setValuesAD(initialUserStateAD);
      }
    } finally {
      setIsDeletingItem(false);
      setHandleDeleteAD({ showDeletePopupAD: false });
    }
  };

  const renderDeleteADDialogue = () => (
    <Dialog aria-labelledby="confirmation-dialog-title" disableBackdropClick disableEscapeKeyDown maxWidth="xs" open={handledeleteAD.showDeletePopupAD}>
      <DialogTitle id="confirmation-dialog-title">Patient MedicalHx Delete Confirmation</DialogTitle>
      <DialogContent><DialogContentText>Are you sure you want to delete advance directive?</DialogContentText></DialogContent>
      <DialogActions>
        <Button color="primary" disabled={isDeletingItem} onClick={() => handleDeleteADOk(handledeleteAD.itemId)} style={{ minWidth: 64 }}>
          {isDeletingItem ? <CircularProgress size={18} color="inherit" /> : 'Yes'}
        </Button>
        <Button color="primary" disabled={isDeletingItem} onClick={handleCancelDeleteAD}>No</Button>
      </DialogActions>
    </Dialog>
  );
  // #endregion

  // ---------------------------------------------------------------------------
  // #region Additional Items
  // ---------------------------------------------------------------------------
  const initialUserStateAI = { id: 0, description: '' };
  const [valuesAI, setValuesAI] = useState(initialUserStateAI);
  const [editvaluesAI, setEditValuesAI] = useState(initialUserStateAI);
  const [AI, setAI] = useState(null);
  const [isaddAI, setIsAddAI] = useState(false);
  const [isEditAI, setIsEditAI] = useState(false);
  const [loadingAI, setLoadingAI] = useState(null);

  const handleFieldChangeAI = (field, value) => setValuesAI({ ...valuesAI, [field]: value });
  const handleEditFieldChangeAI = (field, value) => setEditValuesAI({ ...editvaluesAI, [field]: value });

  const handleAddAI = data => {
    setIsAddAI(true);
    if (data) setValuesAI({ id: data.id, description: data.description });
  };
  const handleAddAICancel = () => { setIsAddAI(false); setValuesAI(initialUserStateAI); };

  const handleSaveAdditionalItems = async () => {
    if (valuesAI.description) {
      setLoadingAI('save');
      try {
        const res = await props.saveAdditionalItems(patientId, valuesAI);
        if (res.status) {
          if (res.additionalItems) {
            setAI(res.additionalItems);
            getPatientMedicalHistory(patientId);
          } else {
            setAI(prev => [...(prev || []), { ...valuesAI, id: res.id || Date.now() }]);
            getPatientMedicalHistory(patientId);
          }
          setValuesAI(initialUserStateAI);
        }
      } finally {
        setLoadingAI(null);
        setIsAddAI(false);
      }
    } else {
      setIsAddAI(false);
    }
  };

  const handleUpdateAdditionalItems = async idx => {
    if (editvaluesAI.description) {
      setLoadingAI('update');
      try {
        const res = await props.updateAdditionalItems(patientId, editvaluesAI);
        if (res.status) {
          setAI(prev => prev.map(item =>
            item.id === editvaluesAI.id ? { ...item, description: editvaluesAI.description } : item
          ));
          getPatientMedicalHistory(patientId);
          setIsEditAI(false);
        }
      } finally {
        setLoadingAI(null);
      }
    }
  };

  const handleEditAdditionalItems = (data, index) => {
    setIsEditAI(true);
    if (data) setEditValuesAI({ id: data.id, description: data.description, editIndex: index });
  };
  const clearAdditionalItems = () => { setIsEditAI(false); setValuesAI(initialUserStateAI); };
  const _handleAddAIClick = event => { event.stopPropagation(); handleAddAI(); };

  const [handledeleteAI, setHandleDeleteAI] = useState({ showDeletePopupAI: false, itemId: '' });
  const handleOpenDeleteAI = id => setHandleDeleteAI({ showDeletePopupAI: true, itemId: id });
  const handleCancelDeleteAI = () => setHandleDeleteAI({ showDeletePopupAI: false });

  const handleDeleteAIOk = async itemId => {
    setIsDeletingItem(true);
    try {
      const res = await props.deleteAdditionalItems(patientId, itemId);
      if (res.status) {
        if (res.additionalItems) {
          setAI(res.additionalItems);
        } else {
          setAI(prev => prev.filter(item => item.id !== itemId));
          getPatientMedicalHistory(patientId);
        }
        setValuesAI(initialUserStateAI);
      }
    } finally {
      setIsDeletingItem(false);
      setHandleDeleteAI({ showDeletePopupAI: false });
    }
  };

  const renderDeleteAIDialogue = () => (
    <Dialog aria-labelledby="confirmation-dialog-title" disableBackdropClick disableEscapeKeyDown maxWidth="xs" open={handledeleteAI.showDeletePopupAI}>
      <DialogTitle id="confirmation-dialog-title">Patient MedicalHx Delete Confirmation</DialogTitle>
      <DialogContent><DialogContentText>Are you sure you want to delete additional item?</DialogContentText></DialogContent>
      <DialogActions>
        <Button color="primary" disabled={isDeletingItem} onClick={() => handleDeleteAIOk(handledeleteAI.itemId)} style={{ minWidth: 64 }}>
          {isDeletingItem ? <CircularProgress size={18} color="inherit" /> : 'Yes'}
        </Button>
        <Button color="primary" disabled={isDeletingItem} onClick={handleCancelDeleteAI}>No</Button>
      </DialogActions>
    </Dialog>
  );
  // #endregion

  // ---------------------------------------------------------------------------
  // #region Health Maintenance
  // ---------------------------------------------------------------------------
  const initialUserStateHM = { id: 0, name: '', recordedDate: new Date(), findings: '' };
  const [valuesHM, setValuesHM] = useState(initialUserStateHM);
  const [editvaluesHM, setEditValuesHM] = useState(initialUserStateHM);
  const [HM, setHM] = useState(null);
  const [isaddHM, setIsAddHM] = useState(false);
  const [isEditHM, setIsEditHM] = useState(false);
  const [loadingHM, setLoadingHM] = useState(null);

  const handleFieldChangeHM = (field, value) => setValuesHM({ ...valuesHM, [field]: value });
  const handleEditFieldChangeHM = (field, value) => setEditValuesHM({ ...editvaluesHM, [field]: value });

  const handleAddHM = data => {
    setIsAddHM(true);
    if (data) setValuesHM({ id: data.id, name: data.name, recordedDate: data.recordedDate, findings: data.findings });
  };
  const handleAddHMCancel = () => { setIsAddHM(false); setValuesHM(initialUserStateHM); };

  const handleSaveHealthMaintenance = async () => {
    if (valuesHM.name || valuesHM.findings) {
      setLoadingHM('save');
      try {
        const res = await props.saveHealthMaintenance(patientId, valuesHM);
        if (res.status) {
          if (res.healthMaintenance) {
            setHM(res.healthMaintenance);
            getPatientMedicalHistory(patientId);
          } else {
            setHM(prev => [...(prev || []), { ...valuesHM, id: res.id || Date.now() }]);
            getPatientMedicalHistory(patientId);
          }
          setValuesHM(initialUserStateHM);
        }
      } finally {
        setLoadingHM(null);
        setIsAddHM(false);
      }
    } else {
      setIsAddHM(false);
    }
  };

  const handleUpdateHealthMaintenance = async idx => {
    if (editvaluesHM.name || editvaluesHM.findings) {
      setLoadingHM('update');
      try {
        const res = await props.updateHealthMaintenance(patientId, editvaluesHM);
        if (res.status) {
          setHM(prev => prev.map(item =>
            item.id === editvaluesHM.id
              ? { ...item, name: editvaluesHM.name, recordedDate: editvaluesHM.recordedDate, findings: editvaluesHM.findings }
              : item
          ));
          getPatientMedicalHistory(patientId);
          setIsEditHM(false);
        }
      } finally {
        setLoadingHM(null);
      }
    }
  };

  const handleEditHealthMaintenance = (data, index) => {
    setIsEditHM(true);
    if (data)
      setEditValuesHM({ id: data.id, name: data.name, recordedDate: data.recordedDate, findings: data.findings, editIndex: index });
  };
  const clearHealthMaintenance = () => { setIsEditHM(false); setValuesHM(initialUserStateHM); };
  const _handleAddHMClick = event => { event.stopPropagation(); handleAddHM(); };

  const [handledeleteHM, setHandleDeleteHM] = useState({ showDeletePopupHM: false, itemId: '' });
  const handleOpenDeleteHM = id => setHandleDeleteHM({ showDeletePopupHM: true, itemId: id });
  const handleCancelDeleteHM = () => setHandleDeleteHM({ showDeletePopupHM: false });

  const handleDeleteHMOk = async itemId => {
    setIsDeletingItem(true);
    try {
      const res = await props.deleteHealthMaintenance(patientId, itemId, props.activeTab);
      if (res.status) {
        if (res.healthMaintenance) {
          setHM(res.healthMaintenance);
        } else {
          setHM(prev => prev.filter(item => item.id !== itemId));
          getPatientMedicalHistory(patientId);
        }
        setValuesHM(initialUserStateHM);
      }
    } finally {
      setIsDeletingItem(false);
      setHandleDeleteHM({ showDeletePopupHM: false });
    }
  };

  const renderDeleteHMDialogue = () => (
    <Dialog aria-labelledby="confirmation-dialog-title" disableBackdropClick disableEscapeKeyDown maxWidth="xs" open={handledeleteHM.showDeletePopupHM}>
      <DialogTitle id="confirmation-dialog-title">Patient MedicalHx Delete Confirmation</DialogTitle>
      <DialogContent><DialogContentText>Are you sure you want to delete health maintenance?</DialogContentText></DialogContent>
      <DialogActions>
        <Button color="primary" disabled={isDeletingItem} onClick={() => handleDeleteHMOk(handledeleteHM.itemId)} style={{ minWidth: 64 }}>
          {isDeletingItem ? <CircularProgress size={18} color="inherit" /> : 'Yes'}
        </Button>
        <Button color="primary" disabled={isDeletingItem} onClick={handleCancelDeleteHM}>No</Button>
      </DialogActions>
    </Dialog>
  );
  // #endregion

  // ---------------------------------------------------------------------------
  // #region Vaccination
  // ---------------------------------------------------------------------------
  const initialUserStateVC = { id: 0, name: '', dategiven: new Date(), lot: '' };
  const [valuesVC, setValuesVC] = useState(initialUserStateVC);
  const [editvaluesVC, setEditValuesVC] = useState(initialUserStateVC);
  const [VC, setVC] = useState(null);
  const [isaddVC, setIsAddVC] = useState(false);
  const [isEditVC, setIsEditVC] = useState(false);
  const [loadingVC, setLoadingVC] = useState(null);

  const handleFieldChangeVC = (field, value) => setValuesVC({ ...valuesVC, [field]: value });
  const handleEditFieldChangeVC = (field, value) => setEditValuesVC({ ...editvaluesVC, [field]: value });

  const handleAddVC = data => {
    setIsAddVC(true);
    if (data) setValuesVC({ id: data.id, name: data.name, dategiven: data.dategiven, lot: data.lot });
  };
  const handleAddVCCancel = () => { setIsAddVC(false); setValuesVC(initialUserStateVC); };

  const handleSaveVaccinations = async () => {
    if (valuesVC.name || valuesVC.lot) {
      setLoadingVC('save');
      try {
        const res = await props.saveVaccinations(patientId, valuesVC);
        if (res.status) {
          if (res.vaccinations) {
            setVC(res.vaccinations);
            getPatientMedicalHistory(patientId);
          } else {
            setVC(prev => [...(prev || []), { ...valuesVC, id: res.id || Date.now() }]);
            getPatientMedicalHistory(patientId);
          }
          setValuesVC(initialUserStateVC);
        }
      } finally {
        setLoadingVC(null);
        setIsAddVC(false);
      }
    } else {
      setIsAddVC(false);
    }
  };

  const handleUpdateVaccinations = async idx => {
    if (editvaluesVC.name || editvaluesVC.lot) {
      setLoadingVC('update');
      try {
        const res = await props.updateVaccinations(patientId, editvaluesVC);
        if (res.status) {
          setVC(prev => prev.map(item =>
            item.id === editvaluesVC.id
              ? { ...item, name: editvaluesVC.name, dategiven: editvaluesVC.dategiven, lot: editvaluesVC.lot }
              : item
          ));
          getPatientMedicalHistory(patientId);
          setIsEditVC(false);
        }
      } finally {
        setLoadingVC(null);
      }
    }
  };

  const handleEditVaccinations = (data, index) => {
    setIsEditVC(true);
    if (data)
      setEditValuesVC({ id: data.id, name: data.name, dategiven: data.dategiven, lot: data.lot, editIndex: index });
  };
  const clearVaccinations = () => { setIsEditVC(false); setValuesVC(initialUserStateVC); };
  const _handleAddVCClick = event => { event.stopPropagation(); handleAddVC(); };

  const [handledeleteVC, setHandleDeleteVC] = useState({ showDeletePopupVC: false, itemId: '' });
  const handleOpenDeleteVC = id => setHandleDeleteVC({ showDeletePopupVC: true, itemId: id });
  const handleCancelDeleteVC = () => setHandleDeleteVC({ showDeletePopupVC: false });

  const handleDeleteVCOk = async itemId => {
    setIsDeletingItem(true);
    try {
      const res = await props.deleteVaccinations(patientId, itemId);
      if (res.status) {
        if (res.vaccinations) {
          setVC(res.vaccinations);
        } else {
          setVC(prev => prev.filter(item => item.id !== itemId));
          getPatientMedicalHistory(patientId);
        }
        setValuesVC(initialUserStateVC);
      }
    } finally {
      setIsDeletingItem(false);
      setHandleDeleteVC({ showDeletePopupVC: false });
    }
  };

  const renderDeleteVaccinationDialogue = () => (
    <Dialog aria-labelledby="confirmation-dialog-title" disableBackdropClick disableEscapeKeyDown maxWidth="xs" open={handledeleteVC.showDeletePopupVC}>
      <DialogTitle id="confirmation-dialog-title">Patient MedicalHx Delete Confirmation</DialogTitle>
      <DialogContent><DialogContentText>Are you sure you want to delete vaccination?</DialogContentText></DialogContent>
      <DialogActions>
        <Button color="primary" disabled={isDeletingItem} onClick={() => handleDeleteVCOk(handledeleteVC.itemId)} style={{ minWidth: 64 }}>
          {isDeletingItem ? <CircularProgress size={18} color="inherit" /> : 'Yes'}
        </Button>
        <Button color="primary" disabled={isDeletingItem} onClick={handleCancelDeleteVC}>No</Button>
      </DialogActions>
    </Dialog>
  );
  // #endregion

  // ---------------------------------------------------------------------------
  // #region Medication  (unchanged — kept as-is per requirement)
  // ---------------------------------------------------------------------------
  const initialUserStateMed = { id: 0, name: '', dose: '', directions: '' };
  const [valuesMed, setValuesMed] = useState(initialUserStateMed);
  const [editvaluesMed, setEditValuesMed] = useState(initialUserStateMed);
  const med = ptActiveMedication || [];
  const inactivemed = ptInactiveMedication || [];
  const [isaddMed, setIsAddMed] = useState(false);
  const [isEditMed, setIsEditMed] = useState(false);
  const [handledeleteMed, setHandleDeleteMed] = useState({ showDeletePopupMed: false, itemId: '' });
  const [loadingMedItemId, setLoadingMedItemId] = useState(null);

  const handleFieldChangeMed = (field, value) => setValuesMed({ ...valuesMed, [field]: value });
  const handleEditFieldChangeMed = (field, value) => setEditValuesMed({ ...editvaluesMed, [field]: value });

  const handleAddMed = data => {
    setIsAddMed(true);
    if (data) setValuesMed({ id: data.id, name: data.name, dose: data.dose, directions: data.directions });
  };
  const handleAddMedCancel = () => { setIsAddMed(false); setValuesMed(initialUserStateMed); };

  const handleSaveMedication = async () => {
    if (valuesMed.name || valuesMed.dose || valuesMed.directions) {
      setLoadingMedItemId('save');
      try {
        const res = await props.saveMedication(patientId, valuesMed);
        if (res.status) {
          await Promise.all([getPatientInactiveMedication(patientId), getPatientActiveMedications(patientId)]);
          setValuesMed(initialUserStateMed);
        }
      } finally {
        setLoadingMedItemId(null);
        setIsAddMed(false);
      }
    } else {
      setIsAddMed(false);
    }
  };

  const handleUpdateMedication = async idx => {
    if (editvaluesMed.name || editvaluesMed.dose) {
      setLoadingMedItemId('update');
      try {
        const res = await props.updateMedication(patientId, editvaluesMed);
        if (res.status) {
          await Promise.all([getPatientInactiveMedication(patientId), getPatientActiveMedications(patientId)]);
          setIsEditMed(false);
        }
      } finally {
        setLoadingMedItemId(null);
      }
    }
  };

  const handleEditMedication = (data, index) => {
    setIsEditMed(true);
    if (data)
      setEditValuesMed({ id: data.id, name: data.name, dose: data.dose, directions: data.directions, editIndex: index });
  };

  const handleInactivateMedication = async item => {
    if (loadingMedItemId !== null) return;
    setLoadingMedItemId(item.id);
    try {
      await props.inactivateMedication(patientId, item);
      await Promise.all([getPatientInactiveMedication(patientId), getPatientActiveMedications(patientId)]);
    } finally {
      setLoadingMedItemId(null);
    }
  };

  const handleActivateMedication = async item => {
    if (loadingMedItemId !== null) return;
    setLoadingMedItemId(item.id);
    try {
      await props.activateMedication(patientId, item);
      await Promise.all([getPatientInactiveMedication(patientId), getPatientActiveMedications(patientId)]);
    } finally {
      setLoadingMedItemId(null);
    }
  };

  const clearMedication = () => { setIsEditMed(false); setValuesMed(initialUserStateMed); };
  const _handleAddMedClick = event => { event.stopPropagation(); handleAddMed(); };

  const handleOpenDeleteMed = id => setHandleDeleteMed({ showDeletePopupMed: true, itemId: id });
  const handleCancelDeleteMed = () => setHandleDeleteMed({ showDeletePopupMed: false, itemId: '' });

  const handleDeleteMedOk = async itemId => {
    setLoadingMedItemId('delete');
    try {
      const res = await props.deleteMedication(patientId, itemId);
      if (res.status) {
        await Promise.all([getPatientInactiveMedication(patientId), getPatientActiveMedications(patientId)]);
      }
    } finally {
      setLoadingMedItemId(null);
      setHandleDeleteMed({ showDeletePopupMed: false, itemId: '' });
    }
  };

  const renderDeleteMedicationDialogue = () => {
    const isDeleting = loadingMedItemId === 'delete';
    return (
      <Dialog aria-labelledby="confirmation-dialog-title" disableBackdropClick disableEscapeKeyDown maxWidth="xs" open={handledeleteMed.showDeletePopupMed}>
        <DialogTitle id="confirmation-dialog-title">Patient MedicalHx Delete Confirmation</DialogTitle>
        <DialogContent><DialogContentText>Are you sure you want to delete medication?</DialogContentText></DialogContent>
        <DialogActions>
          <Button color="primary" disabled={isDeleting} onClick={() => handleDeleteMedOk(handledeleteMed.itemId)} style={{ minWidth: 64 }}>
            {isDeleting ? <CircularProgress size={18} color="inherit" /> : 'Yes'}
          </Button>
          <Button color="primary" disabled={isDeleting} onClick={handleCancelDeleteMed}>No</Button>
        </DialogActions>
      </Dialog>
    );
  };
  // #endregion

  // ---------------------------------------------------------------------------
  // Sync Redux → local state
  // KEY FIX: removed "length > 0" guard on every section so deletions that
  // result in an empty array (or a Redux refresh) always update local state.
  // ---------------------------------------------------------------------------
  useEffect(() => { 
    if (ptMedicalHistory?.allergies) setAllergy(ptMedicalHistory.allergies);
    if (ptMedicalHistory?.socialHistory) setSH(ptMedicalHistory.socialHistory);
    if (ptMedicalHistory?.familyHistory) setFH(ptMedicalHistory.familyHistory);
    if (ptMedicalHistory?.advanceDirective) setAD(ptMedicalHistory.advanceDirective);
    if (ptMedicalHistory?.additionalItems) setAI(ptMedicalHistory.additionalItems);
    if (ptMedicalHistory?.healthMaintenance) setHM(ptMedicalHistory.healthMaintenance);
    if (ptMedicalHistory?.vaccinations) setVC(ptMedicalHistory.vaccinations);
  }, [ptMedicalHistory]);

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  return (
    <Grid className={classes.rightWrap} item xs={12}>
      <PerfectScrollbar key="medical-history-scrollbar" style={{ overflowX: 'hidden', maxHeight: 'calc(100vh - 0px)' }}>
        <div className={clsx(classes.MHXwrap)}>

          {/* ================================================================
              ALLERGIES
          ================================================================ */}
          <ExpansionPanel className={classes.accordionWrap} defaultExpanded="true">
            <ExpansionPanelSummary aria-controls="panel1a-content" className={classes.accordionHeader} expandIcon={<ExpandMore />} id="panel1a-header" style={{ alignItems: 'center' }}>
              <Typography className={classes.heading}>Allergies</Typography>
              <TipButton title="Add New Allergy" onClick={_handleAddAllergyClick} size="small">
                <AddCircleOutlineOutlined className={classes.iconBlue} />
              </TipButton>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Table className={clsx(classes.plainTable, classes.allergyTable)}>
                <TableHead>
                  <TableRow>
                    <TableCell width="40%">Allergy Type</TableCell>
                    <TableCell width="calc(60% - 100px)">Reaction</TableCell>
                    <TableCell style={{ width: 100, whiteSpace: 'nowrap' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isaddallergy && (
                    <TableRow>
                      <TableCell>
                        <TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChange('name', e.target.value)} placeholder="Add New Allergy" value={valuesAllergy.name} variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChange('reaction', e.target.value)} placeholder="Add Reaction" value={valuesAllergy.reaction} variant="outlined" />
                      </TableCell>
                      <TableCell align="center">
                        <TipButton title="Save Allergy" className={classes.iconGreen} disabled={loadingAllergy === 'save'} onClick={handleSaveAllergy} size="small">
                          <SaveIcon loading={loadingAllergy === 'save'} />
                        </TipButton>
                        <TipButton title="Cancel" className={classes.iconRed} disabled={loadingAllergy === 'save'} onClick={handleAddAllergyCancel} size="small">
                          <ClearOutlined />
                        </TipButton>
                      </TableCell>
                    </TableRow>
                  )}
                  {allergy && allergy.length > 0 && allergy.sort((a, b) => b.id - a.id).map((item, idx) => (
                    <TableRow key={item.id}>
                      {isEditAllergy && idx === editvaluesAllergy.editIndex ? (
                        <>
                          <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChange('name', e.target.value)} placeholder="Input Name" value={editvaluesAllergy.name} variant="outlined" /></TableCell>
                          <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChange('reaction', e.target.value)} placeholder="Input Reaction" value={editvaluesAllergy.reaction} variant="outlined" /></TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.reaction}</TableCell>
                        </>
                      )}
                      <TableCell align="center">
                        {isEditAllergy && idx === editvaluesAllergy.editIndex ? (
                          <>
                            <TipButton title="Update Allergy" className={classes.iconGreen} disabled={loadingAllergy === 'update'} onClick={() => handleUpdateAllergy(idx)} size="small"><SaveIcon loading={loadingAllergy === 'update'} /></TipButton>
                            <TipButton title="Cancel" className={classes.iconRed} disabled={loadingAllergy === 'update'} onClick={clearAllergy} size="small"><ClearOutlined /></TipButton>
                          </>
                        ) : (
                          <>
                            <TipButton title="Edit Allergy" onClick={() => handleEditAllergy(item, idx)} size="small"><EditOutlined /></TipButton>
                            <TipButton title="Delete Allergy" onClick={() => handleOpenDeleteAllergy(item.id)} size="small"><DeleteOutlined className={classes.delIcon} /></TipButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ExpansionPanelDetails>
          </ExpansionPanel>

          {/* ================================================================
              MEDICATIONS
          ================================================================ */}
          <ExpansionPanel className={classes.accordionWrap} defaultExpanded="true">
            <ExpansionPanelSummary aria-controls="panel1a-content" className={classes.accordionHeader} expandIcon={<ExpandMore />} id="panel1a-header">
              <Typography className={classes.heading}>Medications</Typography>
              <TipButton title="Add New Medication" onClick={_handleAddMedClick} size="small">
                <AddCircleOutlineOutlined className={classes.iconBlue} />
              </TipButton>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Table className={clsx(classes.plainTable, classes.medicTable)}>
                <TableHead>
                  <TableRow>
                    <TableCell width="40%">Medication</TableCell>
                    <TableCell width="20%">Dose</TableCell>
                    <TableCell width="calc(40% - 100px)">Direction</TableCell>
                    <TableCell align="center" style={{ width: 100, whiteSpace: 'nowrap' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isaddMed && (
                    <TableRow>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeMed('name', e.target.value)} placeholder="Input Name" value={valuesMed.name} variant="outlined" /></TableCell>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeMed('dose', e.target.value)} placeholder="Input dose" value={valuesMed.dose} variant="outlined" /></TableCell>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeMed('directions', e.target.value)} placeholder="Input directions" value={valuesMed.directions} variant="outlined" /></TableCell>
                      <TableCell align="center">
                        <TipButton title="Save Medication" className={classes.iconGreen} disabled={loadingMedItemId === 'save'} onClick={handleSaveMedication} size="small"><SaveIcon loading={loadingMedItemId === 'save'} /></TipButton>
                        <TipButton title="Cancel" className={classes.iconRed} disabled={loadingMedItemId === 'save'} onClick={handleAddMedCancel} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                      </TableCell>
                    </TableRow>
                  )}
                  {med.sort((a, b) => b.id - a.id).map((item, idx) => (
                    <TableRow key={item.id}>
                      {isEditMed && idx === editvaluesMed.editIndex ? (
                        <>
                          <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeMed('name', e.target.value)} placeholder="Input name" value={editvaluesMed.name} variant="outlined" /></TableCell>
                          <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeMed('dose', e.target.value)} placeholder="Input dose" value={editvaluesMed.dose} variant="outlined" /></TableCell>
                          <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeMed('directions', e.target.value)} placeholder="Input directions" value={editvaluesMed.directions} variant="outlined" /></TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{item.name}</TableCell>
                          <TableCell>{item.dose}</TableCell>
                          <TableCell>{item.directions}</TableCell>
                        </>
                      )}
                      <TableCell align="center">
                        {isEditMed && idx === editvaluesMed.editIndex ? (
                          <>
                            <TipButton title="Update Medication" className={classes.iconGreen} disabled={loadingMedItemId === 'update'} onClick={() => handleUpdateMedication(idx)} size="small"><SaveIcon loading={loadingMedItemId === 'update'} /></TipButton>
                            <TipButton title="Cancel" className={classes.iconRed} disabled={loadingMedItemId === 'update'} onClick={clearMedication} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                          </>
                        ) : (
                          <>
                            <TipButton title="Mark as Inactive" disabled={loadingMedItemId !== null} onClick={() => handleInactivateMedication(item)} size="small">
                              {loadingMedItemId === item.id ? <CircularProgress size={16} color="inherit" /> : <CheckCircleOutlineOutlined />}
                            </TipButton>
                            <TipButton title="Edit Medication" disabled={loadingMedItemId !== null} onClick={() => handleEditMedication(item, idx)} size="small"><EditOutlined /></TipButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                  {inactivemed.sort((a, b) => b.id - a.id).map((item, idx) => (
                    <TableRow key={item.id} className={classes.inactiveRow}>
                      <TableCell>{item.name}</TableCell>
                      <TableCell>{item.dose}</TableCell>
                      <TableCell>{item.directions}</TableCell>
                      <TableCell align="center" width={72}>
                        <TipButton title="Mark as Active" disabled={loadingMedItemId !== null} onClick={() => handleActivateMedication(item)} size="small">
                          {loadingMedItemId === item.id ? <CircularProgress size={16} color="inherit" /> : <BlockOutlined style={{ color: '#ccc' }} />}
                        </TipButton>
                        <TipButton title="Delete Medication" disabled={loadingMedItemId !== null} onClick={() => handleOpenDeleteMed(item.id)} size="small"><DeleteOutlined className={classes.delIcon} /></TipButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ExpansionPanelDetails>
          </ExpansionPanel>

          {/* ================================================================
              SOCIAL HISTORY
          ================================================================ */}
          <ExpansionPanel className={classes.accordionWrap} defaultExpanded="true">
            <ExpansionPanelSummary aria-controls="panel1a-content" className={classes.accordionHeader} expandIcon={<ExpandMore />} id="panel1a-header">
              <Typography className={classes.heading}>Social History</Typography>
              <TipButton title="Add Social History" onClick={_handleAddSHClick} size="small">
                <AddCircleOutlineOutlined className={classes.iconBlue} />
              </TipButton>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Table className={clsx(classes.plainTable, classes.socialHTable)}>
                <TableHead>
                  <TableRow>
                    <TableCell>Social History</TableCell>
                    <TableCell style={{ width: 100, whiteSpace: 'nowrap' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isaddSH && (
                    <TableRow>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeSH('description', e.target.value)} placeholder="Input description" value={valuesSH.description} variant="outlined" /></TableCell>
                      <TableCell align="center">
                        <TipButton title="Save Social History" className={classes.iconGreen} disabled={loadingSH === 'save'} onClick={handleSaveSocialHistory} size="small"><SaveIcon loading={loadingSH === 'save'} /></TipButton>
                        <TipButton title="Cancel" className={classes.iconRed} disabled={loadingSH === 'save'} onClick={handleAddSHCancel} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                      </TableCell>
                    </TableRow>
                  )}
                  {SH && SH.length > 0 && SH.sort((a, b) => b.id - a.id).map((item, idx) => (
                    <TableRow key={item.id}>
                      {isEditSH && idx === editvaluesSH.editIndex ? (
                        <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeSH('description', e.target.value)} value={editvaluesSH.description} variant="outlined" /></TableCell>
                      ) : (
                        <TableCell>{item.description}</TableCell>
                      )}
                      <TableCell align="center">
                        {isEditSH && idx === editvaluesSH.editIndex ? (
                          <>
                            <TipButton title="Update Social History" className={classes.iconGreen} disabled={loadingSH === 'update'} onClick={() => handleUpdateSocialHistory(idx)} size="small"><SaveIcon loading={loadingSH === 'update'} /></TipButton>
                            <TipButton title="Cancel" className={classes.iconRed} disabled={loadingSH === 'update'} onClick={clearSocialHistory} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                          </>
                        ) : (
                          <>
                            <TipButton title="Edit Social History" onClick={() => handleEditSocialHistory(item, idx)} size="small"><EditOutlined /></TipButton>
                            <TipButton title="Delete Social History" onClick={() => handleOpenDeleteSH(item.id)} size="small"><DeleteOutlined className={classes.delIcon} /></TipButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ExpansionPanelDetails>
          </ExpansionPanel>

          {/* ================================================================
              HEALTH MAINTENANCE
          ================================================================ */}
          <ExpansionPanel className={classes.accordionWrap} defaultExpanded="true">
            <ExpansionPanelSummary aria-controls="panel1a-content" className={classes.accordionHeader} expandIcon={<ExpandMore />} id="panel1a-header">
              <Typography className={classes.heading}>Health Maintenance</Typography>
              <TipButton title="Add Health Maintenance" onClick={_handleAddHMClick} size="small">
                <AddCircleOutlineOutlined className={classes.iconBlue} />
              </TipButton>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Table className={clsx(classes.plainTable, classes.healthMaint)}>
                <TableHead>
                  <TableRow>
                    <TableCell>Health Maintenance</TableCell>
                    <TableCell>Date Recorded</TableCell>
                    <TableCell>Findings</TableCell>
                    <TableCell style={{ width: 100, whiteSpace: 'nowrap' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isaddHM && (
                    <TableRow>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeHM('name', e.target.value)} placeholder="Input Name" value={valuesHM.name} variant="outlined" /></TableCell>
                      <TableCell>
                        <DatePicker customInput={<TextField className={classes.textField} fullWidth InputProps={{ endAdornment: <InputAdornment position="end"><CalendarToday style={{ height: 16, width: 16 }} /></InputAdornment> }} margin="dense" variant="outlined" />} dateFormat="MM/dd/yyyy" onBlur={e => (e.target.placeholder = 'Date Recorded')} onChange={date => handleFieldChangeHM('recordedDate', date)} onFocus={e => (e.target.placeholder = '')} placeholderText="Date Recorded" selected={valuesHM.recordedDate && new Date(valuesHM.recordedDate)} showMonthDropdown showYearDropdown />
                      </TableCell>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeHM('findings', e.target.value)} placeholder="Input Findings" value={valuesHM.findings} variant="outlined" /></TableCell>
                      <TableCell align="center">
                        <TipButton title="Save Health Maintenance" className={classes.iconGreen} disabled={loadingHM === 'save'} onClick={handleSaveHealthMaintenance} size="small"><SaveIcon loading={loadingHM === 'save'} /></TipButton>
                        <TipButton title="Cancel" className={classes.iconRed} disabled={loadingHM === 'save'} onClick={handleAddHMCancel} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                      </TableCell>
                    </TableRow>
                  )}
                  {HM && HM.length > 0 && HM.sort((a, b) => b.id - a.id).map((item, idx) => (
                    <TableRow key={item.id}>
                      {isEditHM && idx === editvaluesHM.editIndex ? (
                        <>
                          <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeHM('name', e.target.value)} placeholder="Input Name" value={editvaluesHM.name} variant="outlined" /></TableCell>
                          <TableCell>
                            <DatePicker customInput={<TextField className={classes.textField} fullWidth InputProps={{ endAdornment: <InputAdornment position="end"><CalendarToday style={{ height: 16, width: 16 }} /></InputAdornment> }} margin="dense" variant="outlined" />} dateFormat="MM/dd/yyyy" onBlur={e => (e.target.placeholder = 'Date Recorded')} onChange={date => handleEditFieldChangeHM('recordedDate', date)} onFocus={e => (e.target.placeholder = '')} placeholderText="Date Recorded" selected={editvaluesHM.recordedDate && new Date(editvaluesHM.recordedDate)} showMonthDropdown showYearDropdown />
                          </TableCell>
                          <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeHM('findings', e.target.value)} placeholder="Input Findings" value={editvaluesHM.findings} variant="outlined" /></TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{item?.name}</TableCell>
                          <TableCell>{item?.recordedDate ? moment(item.recordedDate).format('MM/DD/YYYY') : ''}</TableCell>
                          <TableCell>{item?.findings}</TableCell>
                        </>
                      )}
                      <TableCell align="center">
                        {isEditHM && idx === editvaluesHM.editIndex ? (
                          <>
                            <TipButton title="Update Health Maintenance" className={classes.iconGreen} disabled={loadingHM === 'update'} onClick={() => handleUpdateHealthMaintenance(idx)} size="small"><SaveIcon loading={loadingHM === 'update'} /></TipButton>
                            <TipButton title="Cancel" className={classes.iconRed} disabled={loadingHM === 'update'} onClick={clearHealthMaintenance} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                          </>
                        ) : (
                          <>
                            <TipButton title="Edit Health Maintenance" onClick={() => handleEditHealthMaintenance(item, idx)} size="small"><EditOutlined /></TipButton>
                            <TipButton title="Delete Health Maintenance" onClick={() => handleOpenDeleteHM(item.id)} size="small"><DeleteOutlined className={classes.delIcon} /></TipButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ExpansionPanelDetails>
          </ExpansionPanel>

          {/* ================================================================
              VACCINATION
          ================================================================ */}
          <ExpansionPanel className={classes.accordionWrap} defaultExpanded="true">
            <ExpansionPanelSummary aria-controls="panel1a-content" className={classes.accordionHeader} expandIcon={<ExpandMore />} id="panel1a-header">
              <Typography className={classes.heading}>Vaccination</Typography>
              <TipButton title="Add Vaccination" onClick={_handleAddVCClick} size="small">
                <AddCircleOutlineOutlined className={classes.iconBlue} />
              </TipButton>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Table className={clsx(classes.plainTable, classes.vaccination)}>
                <TableHead>
                  <TableRow>
                    <TableCell>Vaccinations</TableCell>
                    <TableCell>Date Recorded</TableCell>
                    <TableCell>Comments</TableCell>
                    <TableCell style={{ width: 100, whiteSpace: 'nowrap' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isaddVC && (
                    <TableRow>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeVC('name', e.target.value)} placeholder="Input Name" value={valuesVC.name} variant="outlined" /></TableCell>
                      <TableCell>
                        <DatePicker customInput={<TextField className={classes.textField} fullWidth InputProps={{ endAdornment: <InputAdornment position="end"><CalendarToday style={{ height: 16, width: 16 }} /></InputAdornment> }} margin="dense" variant="outlined" />} dateFormat="MM/dd/yyyy" onBlur={e => (e.target.placeholder = 'Date Given')} onChange={date => handleFieldChangeVC('dategiven', date)} onFocus={e => (e.target.placeholder = '')} placeholderText="Date Given" selected={valuesVC.dategiven && new Date(valuesVC.dategiven)} showMonthDropdown showYearDropdown />
                      </TableCell>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeVC('lot', e.target.value)} placeholder="Comments" value={valuesVC.lot} variant="outlined" /></TableCell>
                      <TableCell align="center">
                        <TipButton title="Save Vaccination" className={classes.iconGreen} disabled={loadingVC === 'save'} onClick={handleSaveVaccinations} size="small"><SaveIcon loading={loadingVC === 'save'} /></TipButton>
                        <TipButton title="Cancel" className={classes.iconRed} disabled={loadingVC === 'save'} onClick={handleAddVCCancel} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                      </TableCell>
                    </TableRow>
                  )}
                  {VC && VC.length > 0 && VC.sort((a, b) => b.id - a.id).map((item, idx) => (
                    <TableRow key={item.id}>
                      {isEditVC && idx === editvaluesVC.editIndex ? (
                        <>
                          <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeVC('name', e.target.value)} placeholder="Input Name" value={editvaluesVC.name} variant="outlined" /></TableCell>
                          <TableCell>
                            <DatePicker customInput={<TextField className={classes.textField} fullWidth InputProps={{ endAdornment: <InputAdornment position="end"><CalendarToday style={{ height: 16, width: 16 }} /></InputAdornment> }} margin="dense" variant="outlined" />} dateFormat="MM/dd/yyyy" onBlur={e => (e.target.placeholder = 'Date Given')} onChange={date => handleEditFieldChangeVC('dategiven', date)} onFocus={e => (e.target.placeholder = '')} placeholderText="Date Given" selected={editvaluesVC.dategiven && new Date(editvaluesVC.dategiven)} showMonthDropdown showYearDropdown />
                          </TableCell>
                          <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeVC('lot', e.target.value)} placeholder="Comments" value={editvaluesVC.lot} variant="outlined" /></TableCell>
                        </>
                      ) : (
                        <>
                          <TableCell>{item?.name}</TableCell>
                          <TableCell>{item?.dategiven ? moment(item.dategiven).format('MM/DD/YYYY') : ''}</TableCell>
                          <TableCell>{item?.lot}</TableCell>
                        </>
                      )}
                      <TableCell align="center">
                        {isEditVC && idx === editvaluesVC.editIndex ? (
                          <>
                            <TipButton title="Update Vaccination" className={classes.iconGreen} disabled={loadingVC === 'update'} onClick={() => handleUpdateVaccinations(idx)} size="small"><SaveIcon loading={loadingVC === 'update'} /></TipButton>
                            <TipButton title="Cancel" className={classes.iconRed} disabled={loadingVC === 'update'} onClick={clearVaccinations} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                          </>
                        ) : (
                          <>
                            <TipButton title="Edit Vaccination" onClick={() => handleEditVaccinations(item, idx)} size="small"><EditOutlined /></TipButton>
                            <TipButton title="Delete Vaccination" onClick={() => handleOpenDeleteVC(item.id)} size="small"><DeleteOutlined className={classes.delIcon} /></TipButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ExpansionPanelDetails>
          </ExpansionPanel>

          {/* ================================================================
              FAMILY HISTORY
          ================================================================ */}
          <ExpansionPanel className={classes.accordionWrap} defaultExpanded="true">
            <ExpansionPanelSummary aria-controls="panel1a-content" className={classes.accordionHeader} expandIcon={<ExpandMore />} id="panel1a-header">
              <Typography className={classes.heading}>Family History</Typography>
              <TipButton title="Add Family History" onClick={_handleAddFHClick} size="small">
                <AddCircleOutlineOutlined className={classes.iconBlue} />
              </TipButton>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Table className={clsx(classes.plainTable, classes.FamilyHistory)}>
                <TableHead>
                  <TableRow>
                    <TableCell>Family History</TableCell>
                    <TableCell style={{ width: 100, whiteSpace: 'nowrap' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isaddFH && (
                    <TableRow>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeFH('description', e.target.value)} placeholder="Input description" value={valuesFH.description} variant="outlined" /></TableCell>
                      <TableCell align="center">
                        <TipButton title="Save Family History" className={classes.iconGreen} disabled={loadingFH === 'save'} onClick={handleSaveFamilyHistory} size="small"><SaveIcon loading={loadingFH === 'save'} /></TipButton>
                        <TipButton title="Cancel" className={classes.iconRed} disabled={loadingFH === 'save'} onClick={handleAddFHCancel} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                      </TableCell>
                    </TableRow>
                  )}
                  {FH && FH.length > 0 && FH.sort((a, b) => b.id - a.id).map((item, idx) => (
                    <TableRow key={item.id}>
                      {isEditFH && idx === editvaluesFH.editIndex ? (
                        <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeFH('description', e.target.value)} value={editvaluesFH.description} variant="outlined" /></TableCell>
                      ) : (
                        <TableCell>{item.description}</TableCell>
                      )}
                      <TableCell align="center">
                        {isEditFH && idx === editvaluesFH.editIndex ? (
                          <>
                            <TipButton title="Update Family History" className={classes.iconGreen} disabled={loadingFH === 'update'} onClick={() => handleUpdateFamilyHistory(idx)} size="small"><SaveIcon loading={loadingFH === 'update'} /></TipButton>
                            <TipButton title="Cancel" className={classes.iconRed} disabled={loadingFH === 'update'} onClick={clearFamilyHistory} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                          </>
                        ) : (
                          <>
                            <TipButton title="Edit Family History" onClick={() => handleEditFamilyHistory(item, idx)} size="small"><EditOutlined /></TipButton>
                            <TipButton title="Delete Family History" onClick={() => handleOpenDeleteFH(item.id)} size="small"><DeleteOutlined className={classes.delIcon} /></TipButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ExpansionPanelDetails>
          </ExpansionPanel>

          {/* ================================================================
              ADVANCE DIRECTIVES
          ================================================================ */}
          <ExpansionPanel className={classes.accordionWrap} defaultExpanded="true">
            <ExpansionPanelSummary aria-controls="panel1a-content" className={classes.accordionHeader} expandIcon={<ExpandMore />} id="panel1a-header">
              <Typography className={classes.heading}>Advance Directives</Typography>
              <TipButton title="Add Advance Directive" onClick={_handleAddADClick} size="small">
                <AddCircleOutlineOutlined className={classes.iconBlue} />
              </TipButton>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Table className={clsx(classes.plainTable)}>
                <TableHead>
                  <TableRow>
                    <TableCell>Advance Directives</TableCell>
                    <TableCell style={{ width: 100, whiteSpace: 'nowrap' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isaddAD && (
                    <TableRow>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeAD('description', e.target.value)} placeholder="Input description" value={valuesAD.description} variant="outlined" /></TableCell>
                      <TableCell align="center">
                        <TipButton title="Save Advance Directive" className={classes.iconGreen} disabled={loadingAD === 'save'} onClick={handleSaveAdvanceDirective} size="small"><SaveIcon loading={loadingAD === 'save'} /></TipButton>
                        <TipButton title="Cancel" className={classes.iconRed} disabled={loadingAD === 'save'} onClick={handleAddADCancel} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                      </TableCell>
                    </TableRow>
                  )}
                  {AD && AD.length > 0 && AD.sort((a, b) => b.id - a.id).map((item, idx) => (
                    <TableRow key={item.id}>
                      {isEditAD && idx === editvaluesAD.editIndex ? (
                        <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeAD('description', e.target.value)} value={editvaluesAD.description} variant="outlined" /></TableCell>
                      ) : (
                        <TableCell>{item.description}</TableCell>
                      )}
                      <TableCell align="center">
                        {isEditAD && idx === editvaluesAD.editIndex ? (
                          <>
                            <TipButton title="Update Advance Directive" className={classes.iconGreen} disabled={loadingAD === 'update'} onClick={() => handleUpdateAdvanceDirective(idx)} size="small"><SaveIcon loading={loadingAD === 'update'} /></TipButton>
                            <TipButton title="Cancel" className={classes.iconRed} disabled={loadingAD === 'update'} onClick={clearAdvanceDirective} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                          </>
                        ) : (
                          <>
                            <TipButton title="Edit Advance Directive" onClick={() => handleEditAdvanceDirective(item, idx)} size="small"><EditOutlined /></TipButton>
                            <TipButton title="Delete Advance Directive" onClick={() => handleOpenDeleteAD(item.id)} size="small"><DeleteOutlined className={classes.delIcon} /></TipButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ExpansionPanelDetails>
          </ExpansionPanel>

          {/* ================================================================
              ADDITIONAL ITEMS
          ================================================================ */}
          <ExpansionPanel className={classes.accordionWrap} defaultExpanded="true">
            <ExpansionPanelSummary aria-controls="panel1a-content" className={classes.accordionHeader} expandIcon={<ExpandMore />} id="panel1a-header">
              <Typography className={classes.heading}>Additional Items</Typography>
              <TipButton title="Add Additional Item" onClick={_handleAddAIClick} size="small">
                <AddCircleOutlineOutlined className={classes.iconBlue} />
              </TipButton>
            </ExpansionPanelSummary>
            <ExpansionPanelDetails>
              <Table className={clsx(classes.plainTable)}>
                <TableHead>
                  <TableRow>
                    <TableCell>Additional Items</TableCell>
                    <TableCell style={{ width: 100, whiteSpace: 'nowrap' }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {isaddAI && (
                    <TableRow>
                      <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleFieldChangeAI('description', e.target.value)} placeholder="Input description" value={valuesAI.description} variant="outlined" /></TableCell>
                      <TableCell align="center">
                        <TipButton title="Save Additional Item" className={classes.iconGreen} disabled={loadingAI === 'save'} onClick={handleSaveAdditionalItems} size="small"><SaveIcon loading={loadingAI === 'save'} /></TipButton>
                        <TipButton title="Cancel" className={classes.iconRed} disabled={loadingAI === 'save'} onClick={handleAddAICancel} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                      </TableCell>
                    </TableRow>
                  )}
                  {AI && AI.length > 0 && AI.sort((a, b) => b.id - a.id).map((item, idx) => (
                    <TableRow key={item.id}>
                      {isEditAI && idx === editvaluesAI.editIndex ? (
                        <TableCell><TextField className={classes.textBox} InputProps={{ classes: { notchedOutline: classes.notchedOutline } }} onChange={e => handleEditFieldChangeAI('description', e.target.value)} value={editvaluesAI.description} variant="outlined" /></TableCell>
                      ) : (
                        <TableCell>{item.description}</TableCell>
                      )}
                      <TableCell align="center">
                        {isEditAI && idx === editvaluesAI.editIndex ? (
                          <>
                            <TipButton title="Update Additional Item" className={classes.iconGreen} disabled={loadingAI === 'update'} onClick={() => handleUpdateAdditionalItems(idx)} size="small"><SaveIcon loading={loadingAI === 'update'} /></TipButton>
                            <TipButton title="Cancel" className={classes.iconRed} disabled={loadingAI === 'update'} onClick={clearAdditionalItems} size="small"><ClearOutlined style={{ color: '#DE5555' }} /></TipButton>
                          </>
                        ) : (
                          <>
                            <TipButton title="Edit Additional Item" onClick={() => handleEditAdditionalItems(item, idx)} size="small"><EditOutlined /></TipButton>
                            <TipButton title="Delete Additional Item" onClick={() => handleOpenDeleteAI(item.id)} size="small"><DeleteOutlined className={classes.delIcon} /></TipButton>
                          </>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ExpansionPanelDetails>
          </ExpansionPanel>

        </div>
      </PerfectScrollbar>

      {handledeleteAllergy && renderDeleteAllergyDialogue()}
      {handledeleteSH && renderDeleteSHDialogue()}
      {handledeleteFH && renderDeleteFHDialogue()}
      {handledeleteAD && renderDeleteADDialogue()}
      {handledeleteAI && renderDeleteAIDialogue()}
      {handledeleteHM && renderDeleteHMDialogue()}
      {handledeleteVC && renderDeleteVaccinationDialogue()}
      {handledeleteMed && renderDeleteMedicationDialogue()}
    </Grid>
  );
};

MHxSoap.propTypes = { className: PropTypes.string };

const mapDispatchToProps = {
  saveAllergy, updateAllergy, deleteAllergy,
  saveSocialHistory, updateSocialHistory, deleteSocialHistory,
  saveFamilyHistory, updateFamilyHistory, deleteFamilyHistory,
  saveAdvanceDirective, updateAdvanceDirective, deleteAdvanceDirective,
  saveAdditionalItems, updateAdditionalItems, deleteAdditionalItems,
  saveHealthMaintenance, updateHealthMaintenance, deleteHealthMaintenance,
  saveVaccinations, updateVaccinations, deleteVaccinations,
  saveMedication, updateMedication, inactivateMedication, activateMedication, deleteMedication,
  getPatientInactiveDiagnosis, getPatientActiveMedications, getPatientMedicalHistory, getPatientInactiveMedication,
};

const mapStateToProps = state => ({
  ptInactiveMedication: state.soap && state.soap.ptInactiveMedication,
  ptActiveMedication: state.soap && state.soap.ptActiveMedication,
  ptMedicalHistory: state.soap && state.soap.ptMedicalHistory,
  ptPastDiagnosis: state.soap && state.soap.ptPastDiagnosis,
  ptInactiveDiagnosis: state.soap && state.soap.ptInactiveDiagnosis,
  ptRemarks: state.soap && state.soap.ptRemarks,
});

export default withRouter(
  connect(mapStateToProps, mapDispatchToProps)(withStyles(styles)(MHxSoap))
);